import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { IDicomEndpoint } from "@/features/servers/schemas/servers";

interface DicomEndpointsEditorProps {
  endpoints: IDicomEndpoint[];
  onChange: (endpoints: IDicomEndpoint[]) => void;
  disabled?: boolean;
}

/**
 * Lista embebida de endpoints DICOM (AETitle / puerto / IP) por instancia de software.
 */
export function DicomEndpointsEditor({
  endpoints,
  onChange,
  disabled = false,
}: DicomEndpointsEditorProps) {
  const update = (index: number, patch: Partial<IDicomEndpoint>) => {
    onChange(endpoints.map((ep, i) => (i === index ? { ...ep, ...patch } : ep)));
  };

  const remove = (index: number) => {
    onChange(endpoints.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...endpoints, { aetitle: "", port: 11112, ip: "", label: "" }]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Endpoints DICOM</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          disabled={disabled}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Agregar
        </Button>
      </div>
      {endpoints.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Sin endpoints. Dejá vacío si el software no usa DICOM (ej. laboratorio).
        </p>
      ) : (
        <div className="space-y-3">
          {endpoints.map((ep, index) => (
            <div
              key={index}
              className="grid gap-2 rounded-md border p-3 sm:grid-cols-2"
            >
              <Input
                placeholder="AETitle"
                value={ep.aetitle}
                onChange={(e) => update(index, { aetitle: e.target.value })}
                disabled={disabled}
              />
              <Input
                placeholder="Puerto"
                type="number"
                value={ep.port}
                onChange={(e) =>
                  update(index, { port: Number(e.target.value) || 0 })
                }
                disabled={disabled}
              />
              <Input
                placeholder="IP"
                value={ep.ip ?? ""}
                onChange={(e) => update(index, { ip: e.target.value })}
                disabled={disabled}
              />
              <div className="flex gap-2">
                <Input
                  placeholder="Label (opcional)"
                  value={ep.label ?? ""}
                  onChange={(e) => update(index, { label: e.target.value })}
                  disabled={disabled}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  disabled={disabled}
                  aria-label="Eliminar endpoint"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
